import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Card, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { productApi } from '../../services/api'; // Adjust path if needed

const ProductFormPage = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const isEditMode = Boolean(productId);

    const [formData, setFormData] = useState({
        name: '',
        product_image_url: '',
        category_id: '',
        bank_id: '', // Use empty string for 'None'/'Optional' initially
        weight: 0,
        bank_code: '',
        product_type: '',
        fee_type: '', // Use empty string for 'None'/'Optional' initially
        fees: '',
        annual_fees: '',
        credit_card_type: '',
        loan_amount: '', // Use empty string for numeric optional fields
        interest_rate: '',
        attributes: '', // Consider JSON editor or key-value pairs UI later
        apply_url: '',
        is_active: true,
    });
    const [banks, setBanks] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [pageLoading, setPageLoading] = useState(isEditMode); // Only load details if editing

    useEffect(() => {
        document.title = isEditMode ? 'Admin - Edit Product' : 'Admin - Add Product';

        const fetchData = async () => {
            setLoading(true);
            setFormError('');
            try {
                const [banksResponse, categoriesResponse] = await Promise.all([
                    productApi.getBanks({ limit: 1000 }),
                    productApi.getCategories({ limit: 1000 })
                ]);
                setBanks(banksResponse.data.items || []);
                setCategories(categoriesResponse.data.items || []);

                if (isEditMode) {
                    const productResponse = await productApi.getProductById(productId);
                    const product = productResponse.data;
                    // Ensure optional fields are handled correctly (null/undefined -> '')
                    setFormData({
                        name: product.name || '',
                        product_image_url: product.product_image_url || '',
                        category_id: product.category?.id || '',
                        bank_id: product.bank?.id || '',
                        weight: product.weight || 0,
                        bank_code: product.bank_code || '',
                        product_type: product.product_type || '',
                        fee_type: product.fee_type ?? '', // Handle potential 0 value
                        fees: product.fees || '',
                        annual_fees: product.annual_fees || '',
                        credit_card_type: product.credit_card_type || '',
                        loan_amount: product.loan_amount ?? '', // Handle potential 0 value
                        interest_rate: product.interest_rate || '',
                        attributes: product.attributes || '', // Keep as string for now
                        apply_url: product.apply_url || '',
                        is_active: product.is_active ?? true,
                    });
                }
            } catch (err) {
                console.error("Error fetching data:", err);
                setFormError(err.response?.data?.detail || err.message || 'Failed to load data.');
            } finally {
                setLoading(false);
                setPageLoading(false);
            }
        };

        fetchData();
    }, [productId, isEditMode]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    // Convert empty strings to null for optional fields before submitting
    const prepareSubmitData = (data) => {
        const preparedData = { ...data };
        for (const key in preparedData) {
            if (preparedData[key] === '') {
                 // Special handling for numeric fields if backend expects null/number
                if (['bank_id', 'weight', 'fee_type', 'loan_amount'].includes(key)) {
                    preparedData[key] = null;
                } else {
                    // Keep other empty strings as they are unless backend requires null
                    // preparedData[key] = null; // Uncomment if backend needs null instead of ""
                }
            }
            // Convert numeric fields from string if necessary
            if (['category_id', 'bank_id', 'weight', 'fee_type', 'loan_amount'].includes(key) && preparedData[key] !== null) {
                 preparedData[key] = Number(preparedData[key]);
            }
        }
        // Remove fields backend doesn't expect or handle appropriately (if any)
        // delete preparedData.some_frontend_only_field;
        return preparedData;
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setFormError('');

        // Basic validation
        if (!formData.name || !formData.category_id) {
            setFormError('Product Name and Category are required.');
            setLoading(false);
            return;
        }

        const submitData = prepareSubmitData(formData);

        try {
            if (isEditMode) {
                await productApi.updateProduct(productId, submitData);
                // Show success message or notification
            } else {
                await productApi.createProduct(submitData);
                 // Show success message or notification
            }
            navigate('/admin/products'); // Redirect to list page on success
        } catch (err) {
            console.error("Error submitting form:", err);
             const errorDetail = err.response?.data?.detail;
             if (Array.isArray(errorDetail)) {
                 // Handle validation errors from FastAPI
                 const formattedErrors = errorDetail.map(detail => `${detail.loc[1]}: ${detail.msg}`).join(', ');
                 setFormError(`Validation Error: ${formattedErrors}`);
             } else {
                 setFormError(errorDetail || err.message || 'An error occurred.');
             }
        } finally {
            setLoading(false);
        }
    };

    if (pageLoading) {
        return (
             
                <Container fluid className="p-4 text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading Product...</span>
                    </Spinner>
                </Container>
             
        );
    }


    return (
        
            <Container fluid className="p-4">
                <Row className="mb-4">
                    <Col>
                        <h2 className="page-title">{isEditMode ? 'Edit Product' : 'Add New Product'}</h2>
                    </Col>
                </Row>
                <Card>
                    <Card.Body>
                        {formError && <Alert variant="danger">{formError}</Alert>}
                        <Form onSubmit={handleSubmit}>
                            <Row>
                                {/* --- Basic Info --- */}
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formProductName">
                                        <Form.Label>Product Name <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter product name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            maxLength={255}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formProductImage">
                                        <Form.Label>Product Image URL</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter image URL"
                                            name="product_image_url"
                                            value={formData.product_image_url}
                                            onChange={handleChange}
                                            maxLength={255}
                                        />
                                         {/* Consider adding image preview */}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formCategory">
                                        <Form.Label>Category <span className="text-danger">*</span></Form.Label>
                                        <Form.Select
                                            name="category_id"
                                            value={formData.category_id}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select Category</option>
                                            {categories.map(cat => (
                                                <option key={cat.id} value={cat.id}>{cat.category}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                 <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formBank">
                                        <Form.Label>Bank</Form.Label>
                                        <Form.Select
                                            name="bank_id"
                                            value={formData.bank_id}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Bank (Optional)</option>
                                            {banks.map(bank => (
                                                <option key={bank.id} value={bank.id}>{bank.bank}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                     <Form.Group className="mb-3" controlId="formBankCode">
                                        <Form.Label>Bank Code</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Bank-specific product code"
                                            name="bank_code"
                                            value={formData.bank_code}
                                            onChange={handleChange}
                                             maxLength={100}
                                        />
                                    </Form.Group>
                                </Col>
                                 <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formProductType">
                                        <Form.Label>Product Type</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="e.g., digital, offline, both"
                                            name="product_type"
                                            value={formData.product_type}
                                            onChange={handleChange}
                                             maxLength={255}
                                        />
                                    </Form.Group>
                                </Col>

                                 {/* --- Financial Details --- */}
                                 <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formFeeType">
                                        <Form.Label>Fee Type</Form.Label>
                                        <Form.Select
                                            name="fee_type"
                                            value={formData.fee_type}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Fee Type (Optional)</option>
                                            <option value="1">Percentage</option>
                                            <option value="2">Decimal/Amount</option>
                                            <option value="0">None/Not Applicable</option> {/* Or handle 0 differently if needed */}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formFees">
                                        <Form.Label>Processing Fees</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="e.g., 1% or 500"
                                            name="fees"
                                            value={formData.fees}
                                            onChange={handleChange}
                                        />
                                    </Form.Group>
                                </Col>
                                 <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formAnnualFees">
                                        <Form.Label>Annual Fees</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="e.g., 1000"
                                            name="annual_fees"
                                            value={formData.annual_fees}
                                            onChange={handleChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formInterestRate">
                                        <Form.Label>Interest Rate</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="e.g., 12% p.a."
                                            name="interest_rate"
                                            value={formData.interest_rate}
                                            onChange={handleChange}
                                             maxLength={255}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formLoanAmount">
                                        <Form.Label>Max Loan Amount</Form.Label>
                                        <Form.Control
                                            type="number"
                                            placeholder="Optional maximum loan amount"
                                            name="loan_amount"
                                            value={formData.loan_amount}
                                            onChange={handleChange}
                                            min="0"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formCreditCardType">
                                        <Form.Label>Credit Card Type</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="e.g., shopping, travel"
                                            name="credit_card_type"
                                            value={formData.credit_card_type}
                                            onChange={handleChange}
                                             maxLength={30}
                                        />
                                    </Form.Group>
                                </Col>

                                {/* --- Other Details --- */}
                                 <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formApplyUrl">
                                        <Form.Label>Apply URL</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Direct application link"
                                            name="apply_url"
                                            value={formData.apply_url}
                                            onChange={handleChange}
                                             maxLength={255}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formWeight">
                                        <Form.Label>Weight/Priority</Form.Label>
                                        <Form.Control
                                            type="number"
                                            placeholder="Sort order (optional)"
                                            name="weight"
                                            value={formData.weight}
                                            onChange={handleChange}
                                            min="0"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={12}>
                                    <Form.Group className="mb-3" controlId="formAttributes">
                                        <Form.Label>Attributes (JSON)</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            placeholder='Enter additional attributes as JSON, e.g., {"feature": "value"}'
                                            name="attributes"
                                            value={formData.attributes}
                                            onChange={handleChange}
                                        />
                                        {/* TODO: Add JSON validation feedback */}
                                    </Form.Group>
                                </Col>
                                <Col md={12}>
                                    <Form.Group className="mb-3" controlId="formIsActive">
                                        <Form.Check
                                            type="checkbox"
                                            label="Product is Active"
                                            name="is_active"
                                            checked={formData.is_active}
                                            onChange={handleChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <div className="mt-3">
                                <Button variant="primary" type="submit" disabled={loading}>
                                    {loading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : (isEditMode ? 'Update Product' : 'Create Product')}
                                </Button>
                                <Link to="/admin/products" className="btn btn-secondary ms-2">
                                    Cancel
                                </Link>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>
            </Container>
        
    );
};

export default ProductFormPage;
